// Reusable button and container style constants for WhiptTranscriptsPanel
// Extracted to reduce code duplication and improve maintainability

export const buttonStyles = {
  // Primary gradient buttons
  primary: "w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm md:text-base font-semibold shadow-lg disabled:opacity-50 flex items-center justify-center gap-2",
  primaryLarge: "w-full px-6 py-3 rounded-xl bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white text-sm font-bold shadow-lg shadow-amber-500/40 disabled:opacity-50 flex items-center justify-center gap-2 hover:scale-[1.02] transition-all",
  
  // Action buttons
  transcribe: "w-full px-3 md:px-4 py-3 md:py-4 rounded-lg bg-gradient-to-r from-amber-600 to-orange-600 text-white text-sm md:text-base font-bold shadow-lg hover:brightness-110 transition-all flex items-center justify-center gap-2",
  stop: "w-full px-3 md:px-4 py-3 md:py-4 rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white text-sm md:text-base font-bold shadow-lg hover:brightness-110 transition-all flex items-center justify-center gap-2",
  paste: "w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-sm md:text-base font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all",
  
  // Small action buttons (header)
  newTranscript: "px-2 md:px-3 py-1.5 rounded-lg border border-purple-400/60 bg-purple-500/10 hover:bg-purple-500/20 text-purple-100 text-xs flex items-center gap-1.5",
  save: "px-2 md:px-3 py-1.5 rounded-lg border border-emerald-400/60 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-100 text-xs flex items-center gap-1.5 disabled:opacity-50",
  copyAll: "px-2 md:px-3 py-1.5 rounded-lg border border-emerald-400/60 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-100 text-xs flex items-center gap-1.5",
  download: "px-2 md:px-3 py-1.5 rounded-lg border border-blue-400/60 bg-blue-500/10 hover:bg-blue-500/20 text-blue-100 text-xs flex items-center gap-1.5",
  
  // Icon buttons
  iconButton: "p-2 hover:bg-slate-800 rounded-lg transition-colors",
  editIcon: "p-1 bg-blue-600/80 hover:bg-blue-600 text-white rounded",
  deleteIcon: "p-1 bg-red-600/80 hover:bg-red-600 text-white rounded",
  
  // Toggle buttons
  toggle: (active: boolean) => 
    `px-3 py-1 rounded-lg text-xs font-medium transition-all ${
      active 
        ? 'bg-purple-600 text-white' 
        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
    }`,
  
  // Minimized floating button
  minimized: "fixed bottom-6 right-24 z-50 px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-full shadow-2xl transition-all hover:scale-105 flex items-center gap-2 group",
};

export const containerStyles = {
  // Main sections
  uploadSection: (isDragging: boolean) =>
    `rounded-xl border-2 ${
      isDragging 
        ? 'border-purple-400 bg-purple-900/40' 
        : 'border-purple-500/30 bg-purple-950/20'
    } p-4 md:p-6 space-y-4 transition-all`,
  
  transcriptSection: "rounded-xl border-2 border-amber-500/30 bg-amber-950/20 p-4 md:p-6 space-y-4",
  pasteSection: "rounded-xl border-2 border-cyan-500/30 bg-cyan-950/20 p-4 md:p-6 space-y-4",
  qaSection: "rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-950/40 via-orange-950/30 to-amber-900/40 p-4 md:p-6 space-y-4",
  
  // Cards
  transcriptCard: (isActive: boolean) =>
    `relative p-3 rounded-lg text-xs transition-all group ${
      isActive
        ? 'bg-amber-900/40 border-2 border-amber-600/50 shadow-lg'
        : 'bg-slate-900/40 border border-slate-700/50 hover:bg-slate-800/50 hover:border-amber-600/30'
    }`,
  
  qaCard: "rounded-xl border border-amber-600/30 bg-gradient-to-br from-amber-900/30 to-orange-900/20 p-3 md:p-4 group hover:border-amber-500/50 transition-all",
  
  // Badges
  activeBadge: "text-[9px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded",
  
  // Sidebars
  mobileSidebar: "absolute left-0 top-0 bottom-0 w-80 bg-gradient-to-br from-slate-900 to-slate-950 border-r border-slate-700 flex flex-col shadow-2xl",
  desktopSidebar: "hidden md:flex md:w-80 border-r border-slate-700 flex-col bg-slate-950/50",
  
  // Inputs
  textarea: "w-full rounded-lg bg-slate-950/70 border border-cyan-500/50 text-slate-200 p-3 text-sm h-32 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/20",
  textareaQA: "w-full rounded-xl bg-slate-950/70 border-2 border-amber-500/50 text-amber-100 p-3 text-sm h-24 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-500/30 placeholder-amber-400/30",
  searchInput: "w-full pl-10 pr-4 py-2 rounded-lg bg-slate-900/50 border border-amber-500/30 text-amber-100 text-sm placeholder-amber-400/30 focus:outline-none focus:border-amber-400",
  titleInput: "flex-1 bg-slate-950/70 border border-amber-500/50 text-slate-200 px-2 py-1 text-xs rounded focus:outline-none focus:border-amber-400",
};

export const layoutStyles = {
  mainPanel: "w-full h-full flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-slate-900 to-slate-950",
  header: "flex items-center justify-between p-3 md:p-6 border-b border-slate-700",
  content: "flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6",
  sidebarHeader: "p-4 border-b border-slate-700 space-y-2",
  sidebarContent: "flex-1 overflow-y-auto p-2 space-y-2",
};
